/* eslint-disable no-prototype-builtins */
/* eslint-disable no-nested-ternary */
/* eslint-disable class-methods-use-this */

import {
  differenceWith, isEqual, isObject, omit, omitBy, pick, pickBy,
} from 'lodash'

import {
  fetchProductLocationsRequest,
  patchProductLocationsRequest,
  fetchProductRequest,
  patchProductRequest,
} from '@/request/globalApi/requests/productRequests'
import {
  fetchHangarProductPlainRequest,
  patchHangarProductPlainRequest,
  fetchHangarProductTechnicalRequest,
  patchHangarProductTechnicalRequest,
  patchParkingProductTechnicalRequest,
  patchOutsideRampsProductTechnicalRequest,
  fetchHangarProductsliceRequest,
  patchHangarProductSliceRequest,
  fetchHangarProductPriceRequest,
  patchHangarProductPriceRequest,
  fetchHangarProductPriceCalendarRequest,
  postHangarProductPriceCalendarRequest,
  fetchHangarProductPriceCalendarByIdRequest,
  patchHangarProductPriceCalendarRequest,
  deleteHangarProductPriceCalendarRequest,
  fetchHangarProductNegotiatedRatesRequest,
  // fetchHangarProductCancellationConditionsRequest,
  // patchHangarProductCancellationConditionsRequest,
  fetchHangarProductNegotiatedRateByIdRequest,
  deleteHangarProductNegotiatedRateByIdRequest,
  postHangarProductNegotiatedRateRequest,
  patchHangarProductNegotiatedRateRequest,
} from '@/request/globalApi/requests/productHangarRequests'
import store from '@/store/store'
import BaseModel from './_Base'

/**
 * Product model
 * @link https://vuemc.io/#basic-usage
 * @link https://vuemc.io/#model-data-access-active ( .sync(), .reset() etc )
 * @example use in /src/views/product/ProductWizard.vue
 */
class ProductHangar extends BaseModel {
  // Default attributes that define the "empty" state.
  defaults() {
    return {
      id: null,
      name: {
        // TODO: dynamic lang init
        fr: '',
        en: '',
      },
      airports: [],
      baseReference: '',
      typology: 'hangar',
      state: '',
      timezone: '',
      vatRate: 0,
      active: false,
      visible: true,
      defaultLang: {
        code: store.state.auth.defaultLang,
      },
      mainCategory: {
        id: null,
        name: {
          // TODO: dynamic lang init
          fr: '',
          en: '',
        },
      },
      mainVariant: {
        id: null,
        attrs: [],
        // TODO: dynamic lang init
        description: { en: '', fr: '' },
        files: [],
        visibilityEndAt: '',
        visibilityStartAt: '',
        technical: {
          loorArea: 0,
          maxDoorHeight: 0,
          maxDoorWidth: 0,
          maxMtow: null,
          maxOccupancyDays: 0,
          minMtow: null,
          minOccupancyDays: 0,
          slices: [],
        },
        packing: {},
        visible: true,
        prices: [],
        priceRanges: [],
        priceRangesFull: [],
        pricingType: '',
        negotiatedRates: [],
        vatRate: 0,
      },
      conditions: [],
      negotiatedRates: [],
    }
  }

  // Attribute mutations to normalize data.
  mutations() {
    return {
      id: id => id || null,
      name: Object,
      airports: airports => airports || [],
      baseReference: String,
      typology: String,
      state: String,
      timezone: String,
      active: Boolean,
      defaultLang: Object,
      mainCategory: Object,
      mainVariant: Object,
      conditions: conditions => conditions || [],
      negotiatedRates: negotiatedRates => negotiatedRates || [],
    }
  }

  //--------------------------------
  //        API REQUESTS
  //--------------------------------

  /**
   * GET /product/:id
   */
  async fetch(id = this.id) {
    await this.checkArgs(id)

    await fetchProductRequest(id).then(({ data }) => {
      const prod = {
        ...data.product,
        mainVariant: { ...this.mainVariant, ...data.product.mainVariant },
      }
      this.set(prod)
      this.sync()
    }).catch(this.catchError)
  }

  /**
   * Edit a product -> PATCH /product/:id
   */
  async patch(productId = this.id) {
    const payload = {
      ...pick(this, 'name', 'visible', 'baseReference', 'timezone'),
      mainCategoryId: this.mainCategory.id,
      defaultLangCode: this.defaultLang.code,
    }
    await patchProductRequest(productId, payload)
  }

  /**
   * GET /product/:id/additional-informations/:variantId
   */
  async fetchPlain(id = this.id, variantId = this.mainVariant.id) {
    await this.checkArgs(id, variantId)

    return fetchHangarProductPlainRequest(id, variantId).then(({ data }) => {
      this.set({
        ...data.product,
        mainVariant: {
          ...this.mainVariant,
          ...omit(data.variant, 'variantAttributes'),
          // ? Trick api, it return an array if no description, or object if has got description
          description: data.variant.description.length !== undefined
            ? {}
            : data.variant.description,
          attrs: data.variant.variantAttributes.map(attr => {
            const normalizeAttr = {
              ...attr,
              type: attr.hasOwnProperty('selectValue')
                ? 'select'
                : attr.hasOwnProperty('choiceValues')
                  ? 'choice'
                // Delete 'inputValue' after update api
                  : (attr.hasOwnProperty('inputValue') || attr.hasOwnProperty('inputValues'))
                    ? 'input'
                    : undefined,
            }
            // ? NOTE: delete this condition after modification api (s)
            if (normalizeAttr.hasOwnProperty('inputValue')) {
              normalizeAttr.inputValues = normalizeAttr.inputValue
              delete normalizeAttr.inputValue
            }
            return normalizeAttr
          }),
        },
      })
      this.sync()
    }).catch(this.catchError)
  }

  /**
   * Edit the mainVariant of product -> PATCH /product/:id/main-information/:variant_id
   */
  async patchPlain(productId = this.id, variantId = this.mainVariant.id) {
    const payload = {
      ...pick(this.mainVariant, 'visibilityStartAt', 'visibilityEndAt'),
      description: pickBy(this.mainVariant.description, lang => !!lang),
      variantAttributes: this.mainVariant.attrs.map(attr => {
        const values = attr.type === 'select'
          ? { selectValueId: attr.selectValue.id }
          : attr.type === 'choice'
            ? { choiceValueIds: attr.choiceValues.map(choice => choice.id) }
            : { inputValues: attr.inputValues }
        return {
          id: attr.id,
          attributeId: attr.attribute.id,
          type: attr.type,
          ...values,
        }
      }),
    }
    const listToDelete = differenceWith(
      this._reference.mainVariant.files,
      this.mainVariant.files,
      isEqual,
    )
    const listToCreate = differenceWith(
      this.mainVariant.files,
      this._reference.mainVariant.files,
      isEqual,
    )

    const posAlreadyExist = this.mainVariant.files.map(file => file.position)
      // Impossible to replace a position in same request
      .concat(listToDelete.map(file => file.position))
    let position = 0

    payload.deletedFileIds = listToDelete.map(file => file.id)
    payload.files = listToCreate.map(file => {
      // Prevent all positions who already exists
      position += 1
      while (posAlreadyExist.includes(position)) {
        position += 1
      }
      return { ...file, position }
    })

    await patchHangarProductPlainRequest({ productId, variantId }, payload)
    this.sync()
  }

  /**
   * GET /product/:id/variant/:variantId/technical-information
   */
  async fetchTechnical(id = this.id, variantId = this.mainVariant.id) {
    return fetchHangarProductTechnicalRequest(id, variantId).then(({ data }) => {
      const properties = {}
      if (this.typology !== 'parking') properties.slices = Object.values(pickBy(data, isObject))
      this.set({
        id,
        mainVariant: {
          ...this.mainVariant,
          id: data.id,
          technical: {
            ...omitBy(data, 'id', isObject),
            ...properties,
          },
        },
      })
      this.sync()
    }).catch(this.catchError)
  }

  /**
   * PATCH /hangar-product/:id/variant/:variantId/technical-information
   */
  async patchTechnical(id = this.id, variantId = this.mainVariant.id) {
    const action = {
      hangar: patchHangarProductTechnicalRequest,
      parking: patchParkingProductTechnicalRequest,
      outside_ramps: patchOutsideRampsProductTechnicalRequest,
    }
    return action[this.typology](id, variantId, this.mainVariant.technical)
      .then(this.sync())
      .catch(this.catchError)
  }

  /**
   * GET /hangar-product/:id/variant-slice-id/:variantId
   */
  async fetchSlice(sliceId, id = this.id) {
    return fetchHangarProductsliceRequest(id, sliceId).then(({ data }) => {
      this.set({
        mainVariant: {
          ...this.mainVariant,
          technical: {
            ...this.mainVariant.technical,
            slices: this.mainVariant.technical.slices.map(slice => {
              if (slice.id !== sliceId) return slice
              return data
            }),
          },
        },
      })
      this.sync()
      return data
    })
      .catch(this.catchError)
  }

  /**
   * PATCH /hangar-product/:id/variant-slice-id/:variantId
   */
  async patchSlice(slice, id = this.id) {
    return patchHangarProductSliceRequest(id, slice)
      .then(this.sync())
      .catch(this.catchError)
  }

  /**
   * GET /hangar-product/:id/variant/:variantId/price-management
   */
  async fetchPrice(id = this.id, variantId = this.mainVariant.id) {
    await this.checkArgs(id, variantId)

    return fetchHangarProductPriceRequest(id, variantId).then(({ data }) => {
      this.set({
        ...data.product,
        mainVariant: { ...this.mainVariant, ...data.variant },
      })
      this.sync()
    }).catch(this.catchError)
  }

  /**
   * PATCH /hangar-product/:id/variant/:variantId/price-management
   */
  async patchPrice(formData = null, id = this.id, variantId = this.mainVariant.id) {
    let prices = this.mainVariant.prices.filter(price => !!price.sellingPriceExcludingTaxes)
    if (this.mainVariant.pricingType === 'unit_price') {
      prices = []
      const periods = ['daily', 'weekly', 'monthly', 'annual']
      periods.forEach(period => {
        const pricePerPeriod = this.mainVariant.prices.filter(price => price.periodicity === period)
        if (pricePerPeriod[0] && pricePerPeriod[0].sellingPriceExcludingTaxes) {
          // Push only all prices !== null or 0
          prices.push(pricePerPeriod[0])
        }
      })
    }

    const payload = {
      ...pick(this.mainVariant, 'pricingType', 'visible', 'vatRate'),
      packingId: this.mainVariant.packing ? this.mainVariant.packing.id : null,
      prices,
    }
    await patchHangarProductPriceRequest(id, variantId, formData || payload)
    this.sync()
  }

  /**
   * GET /hangar-product/:id/variant/:variantId/price-management/calendar
   */
  async fetchPriceCalendar(queryParams = {}, id = this.id, variantId = this.mainVariant.id) {
    await this.checkArgs(id, variantId)

    return fetchHangarProductPriceCalendarRequest(queryParams, id, variantId).then(({ data }) => {
      this.set({
        mainVariant: { ...this.mainVariant, ...data, prices: [] },
      })
      this.sync()
    }).catch(this.catchError)
  }

  /**
   * GET /hangar-product/:id/variant/:variantId/price-management/calendar/:rangeId
   */
  async fetchPriceCalendarById(rangeId, id = this.id, variantId = this.mainVariant.id) {
    await this.checkArgs(rangeId, id, variantId)

    return fetchHangarProductPriceCalendarByIdRequest(id, variantId, rangeId).then(({ data }) => {
      this.set({
        mainVariant: { ...this.mainVariant, priceRangesFull: [data], prices: [] },
      })
      this.sync()
    }).catch(this.catchError)
  }

  /**
   * POST /hangar-product/:id/variant/:variantId/price-management/calendar
   */
  async postPriceCalendar(formData = {}, id = this.id, variantId = this.mainVariant.id) {
    const payload = {
      ...omit(formData, 'id'),
      endAt: formData.endAt ? formData.endAt : null,
      daysOfWeek: formData.daysOfWeek.length
        ? formData.daysOfWeek
        : [0, 1, 2, 3, 4, 5, 6],
    }
    await postHangarProductPriceCalendarRequest(id, variantId, payload)
    // TODO this.set(formdata)
  }

  /**
   * PATCH /hangar-product/:id/variant/:variantId/price-management/calendar/:rangeId
   */
  async patchPriceCalendar(formData = {}, id = this.id, variantId = this.mainVariant.id) {
    const payload = {
      ...formData,
      endAt: formData.endAt ? formData.endAt : null,
      daysOfWeek: formData.daysOfWeek.length
        ? formData.daysOfWeek
        : [0, 1, 2, 3, 4, 5, 6],
    }
    await patchHangarProductPriceCalendarRequest(id, variantId, payload)
  }

  /**
   * DELETE /hangar-product/:id/variant/:variantId/price-management/calendar/:rangeId
   */
  async deletePriceCalendar(rangeId, id = this.id, variantId = this.mainVariant.id) {
    await deleteHangarProductPriceCalendarRequest(id, variantId, rangeId)
  }

  /**
   * GET /product/:id/variant/:variantId/negotiated-rate
   * @returns {Object} pagination
   */
  async fetchNegotiatedRates(params = {}, id = this.id, variantId = this.mainVariant.id) {
    await this.checkArgs(id, variantId)

    return fetchHangarProductNegotiatedRatesRequest(id, variantId, params).then(({ data }) => {
      this.set({
        ...data.product,
        mainVariant: { ...this.mainVariant, ...data.variant },
        negotiatedRates: data.items,
      })
      this.sync()
      return omit(data, 'product', 'variant', 'items')
    }).catch(this.catchError)
  }

  /**
   * GET /product/:id/negotiated-rate/:variantId/:negociatedRateId
   */
  async fetchNegotiatedRateById(id = this.id, variantId = this.mainVariant.id) {
    await this.checkArgs(id, variantId)

    return fetchHangarProductNegotiatedRateByIdRequest(id, variantId).then(({ data }) => {
      this.set({
        ...data.product,
        mainVariant: { ...this.mainVariant, ...data.variant },
      })
      this.sync()
    }).catch(this.catchError)
  }

  /**
   * POST /hangar-product/:id/variant/:variantId/negotiated-rate
   */
  async postNegotiatedRate(payload, productId = this.id, variantId = this.mainVariant.id) {
    postHangarProductNegotiatedRateRequest({ productId, variantId }, payload)
  }

  /**
   * PATCH /hangar-product/:id/variant/:variantId/negotiated-rate/:negotiatedRate
   */
  async patchNegotiatedRate(negotiatedRateId, payload, productId = this.id, variantId = this.mainVariant.id) {
    return patchHangarProductNegotiatedRateRequest({ productId, variantId, negotiatedRateId }, payload)
  }

  /**
   * DELETE /product/:id/variant/:variantId/negotiated-rate/:negociatedRateId
   */
  async deleteNegotiatedRateById(negotiatedRateId, id = this.id, variantId = this.mainVariant.id) {
    await deleteHangarProductNegotiatedRateByIdRequest(id, variantId, negotiatedRateId)
  }

  /**
   * GET /product/:id/price-management/:variantId
   * ? Never used
   */
  // async fetchCancellationConditions(id = this.id, variantId = this.mainVariant.id) {
  //   await this.checkArgs(id, variantId)

  //   return fetchHangarProductCancellationConditionsRequest(id, variantId).then(({ data }) => {
  //     this.set({
  //       ...data.product,
  //       conditions: data.items,
  //       mainVariant: { ...this.mainVariant, ...data.variant },
  //     })
  //     this.sync()
  //   }).catch(this.catchError)
  // }

  /**
   * Cancellation conditions of product -> PATCH /product/:id/cancellation-conditions/:variant_id
   * ? Never used
   */
  // async patchCancellationConditions(productId = this.id, variantId = this.mainVariant.id) {
  //   const payload = {
  //     customConditionEnabled: this.mainVariant.customCancellationPolicyConditionEnabled,
  //     customConditions: this.conditions,
  //     deletedCustomConditionIds: differenceWith(
  //       this._reference.conditions,
  //       this.conditions,
  //       isEqual,
  //     ).map(condition => condition.id),
  //     cancellationPolicyId: this.mainVariant.cancellationPolicy
  //       ? this.mainVariant.cancellationPolicy.id
  //       : null,
  //   }
  //   await patchHangarProductCancellationConditionsRequest({ productId, variantId }, payload)
  //   this.sync()
  // }

  /**
   * GET /package-product/:id/locations
   */
  async fetchLocations(id = this.id) {
    await this.checkArgs(id)

    return fetchProductLocationsRequest(id).then(({ data }) => {
      this.set({
        ...data.product,
        mainVariant: { ...this.mainVariant, ...data.variant },
        airports: data.airportsProducts,
      })
      this.sync()
    }).catch(this.catchError)
  }

  /**
   * Edit the locations of product -> PATCH /package-product/:id/locations
   */
  async patchLocations(productId = this.id) {
    const payload = {
      airportsProducts: this.airports.map(({ active, airport }) => (
        { active, airportId: airport.id }
      )),
    }
    await patchProductLocationsRequest(productId, payload)
  }
}

export default ProductHangar
